import { useEffect, useMemo, useRef } from "react";
import { CREATE_PROJECT_STEPS, PROJECT_FLOW_TYPES } from "../constants";
import { useAppDispatch, useTypedSelector } from "@/config/configureAppStore";
import { useNavigate } from "react-router-dom";
import {
  incrementStep,
  removeFileFromPopUp,
  removeUploadedSampleFileId,
  resetState,
  setCurrentStep,
  setIsCreatingProject
} from "@/reducers/slices/clientProjectPopupSlice/clientProjectPopupSlice";
import {
  setCreateProjectDialog,
  setUploadFilesModal
} from "@/reducers/slices/dialogSlice/dialogSlice";
import { AxiosResponse } from "axios";
import { clientProjectThunk } from "@/reducers/thunks/clientProject";
import { getMixpanelLanguageName, trackMixpanelEvent } from "@/utils/mixpanel";
import { MIXPANEL_EVENTS } from "@/constants/mixpanel";
import { LANGUAGES } from "@/constants/languages";
import { PLANS_CATEGORY } from "@/features/pricing/types";
import { trackUserFlowEvent } from "@/utils/userflow";
import useStartFlow from "@/features/clientHome/hooks/useStartFlow";
import { uploadMultiPartFileApi } from "../api";
import {
  ISampleFile,
  SourceFileResponseList
} from "@/features/clientProjectDetails";
import { useDesktopNotifications } from "@/features/notifications";

const useCreateProject = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const isIcpUser = useTypedSelector((state) => state.globalState.isICPUser);
  const allProjects = useTypedSelector((state) => state.clientHome.allProjects);
  const workspace = useTypedSelector((state) => state.user.activeWorkspaceData);
  const isEnterprise = workspace?.planCategory === PLANS_CATEGORY.CUSTOM;

  const { startFlowCompleted, closeStartFlow, completeStartFlow } =
    useStartFlow();

  const {
    projectName,
    currentStep,
    projectId,
    lipSyncEnabled,
    qualityCheckEnabled,
    sourceLocale,
    targetLocales,
    duplicateFrom,
    isCreatingProject: isLoading,
    selectedSampleFiles,
    sourceFileResponseList
  } = useTypedSelector((state) => state.clientProjectPopup);

  const supportedNonQCTargetLocales = useTypedSelector(
    (state) =>
      state.user?.activeWorkspaceData?.supportedNonQCTargetLocales?.map(
        (locale) => locale.locale
      ) || []
  );
  const _supportedQCTargetLocales = useTypedSelector(
    (state) => state.user?.activeWorkspaceData?.supportedQCTargetLocales
  );
  const supportedQCTargetLocales = useMemo(() => {
    return _supportedQCTargetLocales?.map((locale) => locale.locale) || [];
  }, [_supportedQCTargetLocales]);

  const disableAutomatedDub = useTypedSelector(
    (state) => state.user?.activeWorkspaceData?.disableAutomatedDub || false
  );

  const currentFlowType: PROJECT_FLOW_TYPES = useMemo(() => {
    if (isEnterprise) {
      return PROJECT_FLOW_TYPES.ENTERPRISE;
    }
    if (!isIcpUser) {
      const activeProjectsCount = Object.values(allProjects).filter(
        (p) => !p.archived && !p.draft
      )?.length;
      if (allProjects && !activeProjectsCount)
        return PROJECT_FLOW_TYPES.NON_ICP_NEW;

      return PROJECT_FLOW_TYPES.NON_ICP_EXISTING;
    }
    return PROJECT_FLOW_TYPES.ICP;
  }, [allProjects, isIcpUser, isEnterprise]);

  const flowWiseSteps = useMemo(() => {
    if (currentFlowType === PROJECT_FLOW_TYPES.NON_ICP_NEW) {
      return [
        CREATE_PROJECT_STEPS.UPLOAD_FILES,
        CREATE_PROJECT_STEPS.SELECT_LANGUAGE
      ];
    }
    if (currentFlowType === PROJECT_FLOW_TYPES.ENTERPRISE) {
      return [
        CREATE_PROJECT_STEPS.UPLOAD_FILES,
        CREATE_PROJECT_STEPS.SELECT_LANGUAGE
      ];
    }
    return [
      CREATE_PROJECT_STEPS.UPLOAD_FILES,
      CREATE_PROJECT_STEPS.REQUIREMENTS,
      CREATE_PROJECT_STEPS.SELECT_LANGUAGE
    ];
  }, [currentFlowType]);

  const backStep = useMemo(() => {
    if (currentStep === CREATE_PROJECT_STEPS.UPLOAD_FILES.int) {
      return null;
    }

    let backStep = null;

    if (currentStep === CREATE_PROJECT_STEPS.SELECT_LANGUAGE.int) {
      if (currentFlowType === PROJECT_FLOW_TYPES.ENTERPRISE) {
        backStep = CREATE_PROJECT_STEPS.UPLOAD_FILES;
      } else if (currentFlowType !== PROJECT_FLOW_TYPES.NON_ICP_NEW) {
        backStep = CREATE_PROJECT_STEPS.REQUIREMENTS;
      } else {
        backStep = CREATE_PROJECT_STEPS.UPLOAD_FILES;
      }
    } else if (currentStep === CREATE_PROJECT_STEPS.REQUIREMENTS.int) {
      backStep = CREATE_PROJECT_STEPS.UPLOAD_FILES;
    }

    return backStep;
  }, [currentStep, currentFlowType]);

  const nextStep = useMemo(() => {
    const _nextStep =
      currentStep === CREATE_PROJECT_STEPS.DONE.int
        ? CREATE_PROJECT_STEPS.DONE.int
        : currentStep + 1;
    const _nextDraftState = Object.values(CREATE_PROJECT_STEPS).find(
      (step) => step.int === _nextStep
    );

    return _nextDraftState || CREATE_PROJECT_STEPS.FINISH;
  }, [currentStep]);

  const isNextDisabled = useMemo(() => {
    if (isLoading) {
      return true;
    }

    if (currentStep === CREATE_PROJECT_STEPS.SELECT_LANGUAGE.int) {
      if (!targetLocales?.length || !sourceLocale) {
        return true;
      }

      if (!projectName?.trim()?.length) {
        return true;
      }
    }

    if (currentStep === CREATE_PROJECT_STEPS.REQUIREMENTS.int) {
      if (disableAutomatedDub && !qualityCheckEnabled) {
        return true;
      } else if (qualityCheckEnabled === null) {
        return true;
      }
    }

    return false;
  }, [
    isLoading,
    currentStep,
    targetLocales,
    qualityCheckEnabled,
    disableAutomatedDub,
    sourceLocale,
    projectName
  ]);

  const nextDisabledReason = useMemo(() => {
    if (isLoading) {
      if (projectId) {
        return "Updating Project...";
      }
      return "Creating Project...";
    }

    if (currentStep === CREATE_PROJECT_STEPS.SELECT_LANGUAGE.int) {
      if (!sourceLocale) {
        return "Please select a source language";
      }
      if (!targetLocales?.length) {
        return "Please select at least one target language";
      }

      if (!projectName?.trim()?.length) {
        return "Please enter a project name";
      }
    }

    if (currentStep === CREATE_PROJECT_STEPS.REQUIREMENTS.int) {
      return "Please select a dubbing requirement";
    }

    return "";
  }, [
    isLoading,
    currentStep,
    targetLocales,
    sourceLocale,
    projectName,
    projectId
  ]);

  const setIsLoading = (loading: boolean) => {
    dispatch(setIsCreatingProject(loading));
  };

  const { getNotificationPermission } = useDesktopNotifications();

  const uploadedFilesRef = useRef({
    selectedSampleFiles,
    sourceFileResponseList
  });

  useEffect(() => {
    uploadedFilesRef.current = {
      selectedSampleFiles: selectedSampleFiles || [],
      sourceFileResponseList: sourceFileResponseList || {}
    };
  }, [selectedSampleFiles, sourceFileResponseList]);

  const handleClose = () => {
    dispatch(resetState());
    dispatch(setUploadFilesModal({ isListOfFilesOpen: false }));
    dispatch(setCreateProjectDialog(false));

    if (!startFlowCompleted) {
      closeStartFlow();
      completeStartFlow();
    }
  };
  const handleResponse = (res: AxiosResponse) => {
    if (res?.data?.responseCode === "SUCCESS") {
      if (
        currentStep === CREATE_PROJECT_STEPS.SELECT_LANGUAGE.int ||
        currentStep === CREATE_PROJECT_STEPS.DONE.int
      ) {
        const projectId = res?.data?.responseData?.projectId;
        navigate(`/project/${projectId}?workspaceId=${workspace?.workspaceId}`);
        setIsLoading(false);
        handleClose();
        if (
          uploadedFilesRef?.current?.selectedSampleFiles?.length ||
          uploadedFilesRef?.current?.sourceFileResponseList?.length
        ) {
          getNotificationPermission();
        }
      }
      setIsLoading(false);
      dispatch(incrementStep(1));
    }
  };

  const handleCurrentStep = () => {
    setIsLoading(true);
    const nextDraftState = nextStep?.enum;
    let updatedTargetLocales = targetLocales;
    switch (currentStep) {
      case CREATE_PROJECT_STEPS.UPLOAD_FILES.int:
        if (!projectId) {
          handleCreateProject()
            .then((res) => {
              const projectId = res?.data?.responseData?.projectId;
              console.debug("projectId", projectId, res);
              handleSampleFileUpload(projectId);
              const nextStep = flowWiseSteps[1];
              dispatch(setCurrentStep(nextStep.int));
              setIsLoading(false);
            })
            .catch(() => {
              setIsLoading(false);
            });
        } else {
          const nextStep = flowWiseSteps[1];
          dispatch(setCurrentStep(nextStep.int));
          handleSampleFileUpload(projectId, uploadedFilesRef.current);
          setIsLoading(false);
        }

        break;

      case CREATE_PROJECT_STEPS.SELECT_LANGUAGE.int:
        trackMixpanelEvent(MIXPANEL_EVENTS.DUB_LANGUAGE_SELECT, {
          "Project ID": projectId,
          "Source Language":
            !sourceLocale || sourceLocale === LANGUAGES.AUTO
              ? "Auto-Detect"
              : getMixpanelLanguageName(sourceLocale),
          "Target Languages": targetLocales.map((locale) =>
            getMixpanelLanguageName(locale)
          )
        });

        trackMixpanelEvent(MIXPANEL_EVENTS.DUB_START, {
          "Project ID": projectId
        });

        dispatch(
          clientProjectThunk.updateProject({
            projectId: projectId,
            payload: {
              // draftState:
              //   currentFlowType === PROJECT_FLOW_TYPES.NON_ICP_NEW
              //     ? CREATE_PROJECT_STEPS.DONE.enum
              //     : nextDraftState,
              draftState: CREATE_PROJECT_STEPS.DONE.enum,
              sourceLocale:
                sourceLocale === LANGUAGES.AUTO ? null : sourceLocale,
              targetLocales,
              projectType: "AUTO_DETECT",
              projectName,
              qualityCheckEnabled: Boolean(qualityCheckEnabled)
            }
          })
        ).then((res) => {
          handleResponse(res.payload);
        });
        break;

      case CREATE_PROJECT_STEPS.REQUIREMENTS.int:
        trackMixpanelEvent(MIXPANEL_EVENTS.DUB_REQUIREMENT_SELECT, {
          "Project ID": projectId,
          "Lip Sync": lipSyncEnabled,
          Quality: qualityCheckEnabled ? "QA" : "Automated Dubbing"
        });

        if (qualityCheckEnabled) {
          updatedTargetLocales = targetLocales.filter((locale) =>
            supportedQCTargetLocales.includes(locale)
          );
        } else {
          updatedTargetLocales = targetLocales.filter((locale) =>
            supportedNonQCTargetLocales.includes(locale)
          );
        }

        dispatch(
          clientProjectThunk.updateProject({
            projectId: projectId,
            payload: {
              draftState: nextDraftState,
              lipSyncEnabled,
              qualityCheckEnabled,
              targetLocales: updatedTargetLocales
            }
          })
        )
          .then((res) => {
            handleResponse(res.payload);
          })
          .catch(() => {
            setIsLoading(false);
          });
        break;

      case CREATE_PROJECT_STEPS.FINISH.int:
        trackMixpanelEvent(MIXPANEL_EVENTS.DUB_START, {
          "Project ID": projectId
        });

        dispatch(
          clientProjectThunk.updateProject({
            projectId: projectId,
            payload: {
              draftState: nextDraftState
            }
          })
        ).then((res) => {
          if (res.payload.data.responseCode === "SUCCESS") {
            setIsLoading(false);
            navigate(`/project/${projectId}`);
            dispatch(resetState());
            dispatch(setUploadFilesModal({ isListOfFilesOpen: false }));
            dispatch(setCreateProjectDialog(false));
          }
        });
        break;
      default:
        break;
    }
  };
  const handleBack = () => {
    if (currentStep === CREATE_PROJECT_STEPS.UPLOAD_FILES.int) {
      return;
    }
    if (currentStep === CREATE_PROJECT_STEPS.FINISH.int) {
      dispatch(incrementStep(-1));
      return;
    }

    if (currentStep === CREATE_PROJECT_STEPS.SELECT_LANGUAGE.int) {
      if (currentFlowType === PROJECT_FLOW_TYPES.ENTERPRISE) {
        dispatch(setCurrentStep(CREATE_PROJECT_STEPS.UPLOAD_FILES.int));
      } else if (currentFlowType !== PROJECT_FLOW_TYPES.NON_ICP_NEW) {
        dispatch(setCurrentStep(CREATE_PROJECT_STEPS.REQUIREMENTS.int));
      } else {
        dispatch(setCurrentStep(CREATE_PROJECT_STEPS.UPLOAD_FILES.int));
      }
      return;
    }

    if (currentStep === CREATE_PROJECT_STEPS.REQUIREMENTS.int) {
      dispatch(setCurrentStep(CREATE_PROJECT_STEPS.UPLOAD_FILES.int));
    }
    return;
  };

  const handleCreateProject = () => {
    if (duplicateFrom) {
      return dispatch(
        clientProjectThunk.duplicateProject({
          projectId: duplicateFrom,
          projectName
        })
      ).unwrap();
    }
    if (projectId) {
      return dispatch(
        clientProjectThunk.updateProject({
          projectId,
          payload: {
            projectName,
            draftState: nextStep?.enum
          }
        })
      ).unwrap();
    }

    trackMixpanelEvent(MIXPANEL_EVENTS.CREATE_PROJECT_SUCCESS, {});
    trackUserFlowEvent(MIXPANEL_EVENTS.CREATE_PROJECT_SUCCESS, {});
    return dispatch(
      clientProjectThunk.createProject({
        projectName: projectName || "Untitled Project",
        draftState: [
          PROJECT_FLOW_TYPES.ENTERPRISE,
          PROJECT_FLOW_TYPES.NON_ICP_NEW
        ].includes(currentFlowType)
          ? (CREATE_PROJECT_STEPS.SELECT_LANGUAGE.enum as any)
          : (CREATE_PROJECT_STEPS.REQUIREMENTS.enum as any)
      })
    ).unwrap();
  };

  const handleSampleFileUpload = (
    projectId: string,
    updatedData?: {
      sourceFileResponseList: SourceFileResponseList | undefined;
      selectedSampleFiles: ISampleFile[] | null | undefined;
    } | null
  ) => {
    const toBeAdded: any[] = [];
    const toBeRemoved: string[] = [];
    const isAlreadyUploaded: string[] = [];

    const _selectedSampleFiles = updatedData
      ? updatedData.selectedSampleFiles
      : selectedSampleFiles;
    const _sourceFileResponseList = updatedData
      ? updatedData.sourceFileResponseList
      : sourceFileResponseList;

    const uploadedSampleFiles = Object.values(
      _sourceFileResponseList || {}
    ).filter((file) => file.sourceSampleFileId);

    if (uploadedSampleFiles?.length) {
      uploadedSampleFiles.forEach((file) => {
        const isUploadedFileSelected = _selectedSampleFiles?.filter(
          (sampleFile) => file.sourceSampleFileId === sampleFile.fileId
        );
        if (isUploadedFileSelected?.length) {
          isAlreadyUploaded.push(file.sourceFileId);
        } else {
          toBeRemoved.push(file.sourceFileId);
        }
      });
    }

    if (toBeRemoved?.length || isAlreadyUploaded?.length) {
      _selectedSampleFiles?.forEach((file) => {
        const keyExists = _sourceFileResponseList
          ? [...isAlreadyUploaded, ...toBeRemoved].some((fileId) =>
              [
                _sourceFileResponseList[fileId].originalFileName,
                _sourceFileResponseList[fileId].fileName
              ].includes(file.fileName)
            )
          : false;
        if (!keyExists) {
          toBeAdded.push(file);
        }
      });
    } else if (!uploadedSampleFiles?.length) {
      _selectedSampleFiles?.forEach((file) => {
        toBeAdded.push(file);
      });
    }

    if (toBeRemoved?.length) {
      toBeRemoved.forEach((fileKey) => {
        uploadMultiPartFileApi.removeFile({ fileId: fileKey }).then(() => {
          dispatch(
            removeFileFromPopUp({
              fileId: fileKey,
              projectId: projectId
            })
          );
          if (_sourceFileResponseList?.[fileKey]?.sourceSampleFileId) {
            dispatch(
              removeUploadedSampleFileId(
                _sourceFileResponseList?.[fileKey]?.sourceSampleFileId as string
              )
            );
          }
        });
      });
    }

    if (toBeAdded?.length && projectId) {
      dispatch(
        clientProjectThunk.useSampleFilesInProject({
          projectId,
          sampleFiles: toBeAdded
        })
      );
      toBeAdded.forEach((file) => {
        trackMixpanelEvent(MIXPANEL_EVENTS.FILE_UPLOAD_START, {
          "File Format": file.fileType,
          "Upload Type": "Sample File",
          "Entry Point": "Create Project",
          "Project ID": projectId,
          "Sample File Id": file.fileId
        });
        trackMixpanelEvent(MIXPANEL_EVENTS.FILE_UPLOAD_SUCCESS, {
          "File Format": "VIDEO",
          "Upload Type": "Sample File",
          "Project ID": projectId,
          "Sample File Id": file.fileId
        });
      });
    }
  };

  return {
    currentStep,
    nextStep,
    backStep,
    isNextDisabled,
    nextDisabledReason,
    projectName,
    projectId,
    lipSyncEnabled,
    qualityCheckEnabled,
    sourceLocale,
    targetLocales,
    supportedNonQCTargetLocales,
    supportedQCTargetLocales,
    handleClose,
    handleResponse,
    handleCurrentStep,
    handleBack,
    isLoading,
    setIsLoading,
    currentFlowType,
    handleCreateProject,
    flowWiseSteps,
    handleSampleFileUpload
  };
};

export default useCreateProject;
